import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

// Google API key with Places API enabled
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDL4DCqhF9gQnpQdERZWwZU7sfzcBXQEsQ';

/**
 * AddressAutocomplete component with Google Places API
 * Includes optional second line for address details (suite, cross street, etc.)
 */
const AddressAutocomplete = ({ 
  value = '', 
  value2 = '',
  onChange, 
  onChange2,
  onSelect,
  placeholder = "Enter address...",
  placeholder2 = "Suite #, cross street, or other details",
  showLine2 = false,
  className = "",
  disabled = false,
  id,
  "data-testid": dataTestId
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [inputValue2, setInputValue2] = useState(value2 || '');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [googleError, setGoogleError] = useState(false);
  const initAttemptedRef = useRef(false);

  // Sync props to local state
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    setInputValue2(value2 || '');
  }, [value2]);

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google?.maps?.places) {
        setIsGoogleLoaded(true);
        return;
      }

      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        existingScript.addEventListener('load', () => setIsGoogleLoaded(true));
        existingScript.addEventListener('error', () => setGoogleError(true));
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsGoogleLoaded(true);
      script.onerror = () => {
        console.warn('Google Maps failed to load - using basic input');
        setGoogleError(true);
      };
      document.head.appendChild(script);
    };

    loadGoogleMaps();
  }, []);

  // Initialize autocomplete when Google is loaded
  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current || initAttemptedRef.current || googleError) return;
    initAttemptedRef.current = true;

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        try {
          const place = autocompleteRef.current.getPlace();
          if (place?.formatted_address) {
            setInputValue(place.formatted_address);
            onChange?.(place.formatted_address);
            
            if (onSelect) {
              const addressComponents = {};
              place.address_components?.forEach(component => {
                component.types.forEach(type => {
                  addressComponents[type] = component.long_name;
                  addressComponents[`${type}_short`] = component.short_name;
                });
              });
              
              onSelect({
                formatted_address: place.formatted_address,
                street_number: addressComponents.street_number || '',
                street: addressComponents.route || '',
                city: addressComponents.locality || addressComponents.sublocality || '',
                state: addressComponents.administrative_area_level_1_short || '',
                zip: addressComponents.postal_code || '',
                country: addressComponents.country || '',
                lat: place.geometry?.location?.lat(),
                lng: place.geometry?.location?.lng()
              });
            }
          }
        } catch (err) {
          console.warn('Error processing place selection:', err);
        }
      });
    } catch (error) {
      console.warn('Error initializing Google Places:', error);
      setGoogleError(true);
    }
  }, [isGoogleLoaded, googleError]);

  const handleChange = (e) => {
    const newValue = e.target.value || '';
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleChange2 = (e) => {
    const newValue = e.target.value || '';
    setInputValue2(newValue);
    onChange2?.(newValue);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
          <MapPin className="h-4 w-4" />
        </div>
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={`pl-10 ${className}`}
          disabled={disabled}
          id={id}
          data-testid={dataTestId}
          autoComplete="off"
        />
      </div>
      {showLine2 && (
        <Input
          type="text"
          value={inputValue2}
          onChange={handleChange2}
          placeholder={placeholder2}
          className={className}
          disabled={disabled}
          data-testid={dataTestId ? `${dataTestId}-line2` : undefined}
          autoComplete="off"
        />
      )}
    </div>
  );
};

export default AddressAutocomplete;
